import React from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(BarElement, CategoryScale, LinearScale, Title, Tooltip, Legend);

// Sample data – replace with your real rawData
const rawData = {
  visit: [
    { id: 1, start_datetime: '2025-06-02 10:15:00', visitors: 1, amenity: 'clubhouse-pool--jacuzzi' },
    { id: 2, start_datetime: '2025-06-02 11:00:00', visitors: 2, amenity: 'lounge' },
    { id: 222, start_datetime: '2025-06-02 12:00:00', visitors: 5, amenity: 'lounge' },
    { id: 333, start_datetime: '2025-06-02 13:00:00', visitors: 5, amenity: 'lounge' },
    { id: 333, start_datetime: '2025-06-02 22:00:00', visitors: 5, amenity: 'lounge' },
    { id: 3, start_datetime: '2025-06-03 10:30:00', visitors: 1, amenity: 'clubhouse-pool--jacuzzi' },
    { id: 43, start_datetime: '2025-06-03 21:30:00', visitors: 1, amenity: 'clubhouse-pool--jacuzzi' },
    { id: 4, start_datetime: '2025-06-03 14:05:00', visitors: 1, amenity: 'lounge' },
    { id: 5, start_datetime: '2025-06-04 12:00:00', visitors: 1, amenity: 'clubhouse-gym' },
    { id: 6, start_datetime: '2025-06-04 10:50:00', visitors: 1, amenity: 'clubhouse-pool--jacuzzi' },
    { id: 7, start_datetime: '2025-06-05 16:00:00', visitors: 1, amenity: 'clubhouse-pool--jacuzzi' },
    { id: 8, start_datetime: '2025-06-06 11:00:00', visitors: 1, amenity: 'clubhouse-gym' },
    { id: 9, start_datetime: '2025-06-06 11:30:00', visitors: 2, amenity: 'lounge' },
    { id: 10, start_datetime: '2025-06-07 13:00:00', visitors: 1, amenity: 'lounge' },
    { id: 11, start_datetime: '2025-06-08 13:00:00', visitors: 1, amenity: '22222-pool--jacuzzi' },
    { id: 1, start_datetime: '2025-06-02 10:15:00', visitors: 1, amenity: '22222-pool--jacuzzi' },
    { id: 2, start_datetime: '2025-06-02 11:00:00', visitors: 2, amenity: 'lounge' },
    { id: 222, start_datetime: '2025-06-02 12:00:00', visitors: 5, amenity: 'bbq' },
    { id: 333, start_datetime: '2025-06-02 13:00:00', visitors: 5, amenity: 'bbq' },
    { id: 333, start_datetime: '2025-06-02 22:00:00', visitors: 5, amenity: 'bbq' },
    { id: 3, start_datetime: '2025-06-03 10:30:00', visitors: 1, amenity: '22222-pool--jacuzzi' },
    { id: 43, start_datetime: '2025-06-03 21:30:00', visitors: 1, amenity: '22222-pool--jacuzzi' },
    { id: 4, start_datetime: '2025-06-03 14:05:00', visitors: 1, amenity: 'bbq' },
    { id: 5, start_datetime: '2025-06-04 12:00:00', visitors: 1, amenity: '22222-gym' },
    { id: 6, start_datetime: '2025-06-04 10:50:00', visitors: 1, amenity: '22222-pool--jacuzzi' },
    { id: 7, start_datetime: '2025-06-05 16:00:00', visitors: 1, amenity: '22222-pool--jacuzzi' },
    { id: 8, start_datetime: '2025-06-06 11:00:00', visitors: 1, amenity: '22222-gym' },
    { id: 9, start_datetime: '2025-06-06 11:30:00', visitors: 2, amenity: 'bbq' },
    { id: 10, start_datetime: '2025-06-07 13:00:00', visitors: 1, amenity: 'bbq' },
    { id: 11, start_datetime: '2025-06-08 13:00:00', visitors: 1, amenity: '22222-pool--jacuzzi' },
  ],
};

const PoolVisitsByAmenityStacked = () => {
  const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const amenities = [...new Set(rawData.visit.map(v => v.amenity))];

  // Build nested structure: [day][amenity][hour] = visitorCount
  const visitMatrix = {};

  for (let day of daysOfWeek) {
    visitMatrix[day] = {};
    for (let amenity of amenities) {
      visitMatrix[day][amenity] = new Array(24).fill(0);
    }
  }

  rawData.visit.forEach(({ start_datetime, visitors, amenity }) => {
    const date = new Date(start_datetime);
    const day = daysOfWeek[date.getDay()];
    const hour = date.getHours();
    if (visitMatrix[day] && visitMatrix[day][amenity]) {
      visitMatrix[day][amenity][hour] += visitors;
    }
  });

  // Generate datasets: one for each hour, stack grouped by amenity
  const datasets = [];

  for (let hour = 0; hour < 24; hour++) {
    amenities.forEach((amenity, amenityIndex) => {
      datasets.push({
        label: `${amenity} @ ${hour}:00`,
        data: daysOfWeek.map(day => visitMatrix[day][amenity][hour]),
        backgroundColor: `hsl(${(amenityIndex * 50 + hour * 5) % 360}, 60%, 60%)`,
        stack: amenity, // groups stacks per amenity
      });
    });
  }

  const data = {
    labels: daysOfWeek,
    datasets,
  };

  let options = {
    responsive: true,
    plugins: {
      title: {
        display: true,
        text: 'Visits by Amenity & Hour, Grouped by Day',
      },
      legend: {
        display: false,  // <-- turn off legend here
        position: 'right',
      },
    },
    scales: {
      x: {
        stacked: true,
        title: {
          display: true,
          text: 'Day of the Week',
        },
      },
      y: {
        stacked: true,
        beginAtZero: true,
        title: {
          display: true,
          text: 'Visitor Count',
        },
      },
    },
  };


  return <Bar data={data} options={options} />;
};

export default PoolVisitsByAmenityStacked;
